import { Fragment, Suspense, lazy, memo, useState, useEffect, useRef } from 'react'
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Users } from 'lucide-react'
import { Toaster, toast } from 'sonner'
import {
  exportPlanSettings,
  parsePlanSettingsFile,
} from '@/lib/planSettings'
import { downloadFile, escapeCssValue, flashInputTarget } from '@/lib/utils'
import { FOCUS_EMPLOYEE_INPUT_EVENT } from '@/lib/employeeFocus'
import { importWithReload } from '@/lib/lazyWithReload'
import { CENSUS_MODES } from '@/lib/demoEntitlements'
import { track } from '@/lib/analytics'
import { exportDownloadProps, trackExportEvent, workspaceExportProps } from '@/lib/analytics/exportEvents'
import { REPORT_SECTIONS } from '@/lib/reportConfig'
import { HTML_REPORT_SECTIONS } from '@/lib/htmlExport/interactive'
import { supabaseEnv } from '@/lib/supabaseClient'
import { flushPlanAutosave, restorePlanAutosave, retryPlanAutosave } from '@/lib/planAutosave'
import useStore, { isComparing, WORKSPACE_STAGES } from '@/stores/useStore'
import useClientBook from '@/stores/useClientBook'
import Header from '@/components/Header'
import PlanChips from '@/components/PlanChips'
import DataInput from '@/components/DataInput'
import PlanSettings from '@/components/Calculations'
import PlanInputComparison from '@/components/compare/PlanInputComparison'
import SummaryDashboard from '@/components/SummaryDashboard'
import ChecksStage from '@/components/checks/ChecksStage'
import AllocationComparisonTable from '@/components/plan/AllocationComparisonTable'
import CashBalanceScheduleTable from '@/components/plan/CashBalanceScheduleTable'
import EmployeeDetails from '@/components/EmployeeDetails'
import Visualizations from '@/components/Visualizations'
import UtilityDialogs from '@/components/UtilityDialogs'
import FeatureLockDialog from '@/components/FeatureLockDialog'
import ReportStudio from '@/components/report/ReportStudio'
import { ClientBookBody } from '@/components/clients/ClientSwitcher'
import EstimateModeBanner from '@/components/estimator/EstimateModeBanner'
import EstimatorDialog from '@/components/estimator/EstimatorDialog'
import { SectionNav } from '@/components/calculator/PaneChrome'
import OnboardingFlow from '@/components/onboarding/OnboardingFlow'
import { getPresetSettings, PLAN_PRESETS, presetSettingRows } from '@/lib/planPresets'
import { modeledCheckSummaryForState } from '@/lib/modeledCheckSummary'
import { planDisplayName } from '@/lib/planDisplayNames'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'

// The CFO view keeps the same anchors and only relabels what the section is.
const outputNavItems = (cfoMode) => [
  { href: '#summary-dashboard', label: cfoMode ? 'Cost summary' : 'Summary' },
  { href: '#employee-details', label: 'Employee details' },
  // groupLabel renders a subdued divider before this item, clustering the
  // chart anchors so the nav reads as two tiers instead of ten equal tabs.
  { href: '#visualizations-overview', label: 'Overview', groupLabel: 'Charts' },
  { href: '#visualizations-contributions', label: 'Contributions' },
  { href: '#visualizations-distributions', label: 'Distributions' },
  { href: '#visualizations-relationships', label: 'Relationships' },
  { href: '#visualizations-turnover', label: 'Turnover' },
  { href: '#visualizations-demographics', label: 'Demographics' },
  { href: '#visualizations-loans', label: 'Loans' },
  { href: '#visualizations-owner-savings', label: 'Owner Savings' },
  { href: '#visualizations-custom', label: 'Custom' },
]

// The five workspace destinations stay available after a census exists. They
// are navigation, not a locked wizard, so the shell presents them as compact
// tabs rather than a numbered progress indicator.
// Allocation classes are a census column, so they are edited on Census, not in
// their own stage. Checks sits before Results on purpose: it is where you find out
// whether the numbers on Results can be trusted and presented.
const STAGE_ITEMS = [
  { key: 'people', step: 1, label: 'Employees' },
  { key: 'plan', step: 2, label: 'Plan Settings' },
  { key: 'checks', step: 3, label: 'Checks' },
  { key: 'results', step: 4, label: 'Results' },
  { key: 'reports', step: 5, label: 'Reports' },
]

// Example rows exist in the store by default, so the completion marker
// distinguishes an intentionally chosen demo from an untouched first visit.
const ONBOARDING_DONE_KEY = 'retira-onboarding-done'
// Dev-only preview: append ?force-onboarding to any workspace URL to see the
// first-run flow without clearing storage. Compiled out of production builds.
const FORCE_ONBOARDING = import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('force-onboarding')
const MOBILE_WARNING_SEEN_KEY = 'retira-mobile-warning-seen'

const escapePreviewHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;')

const buildCsvPreviewHtml = (rows) => {
  const [headers = [], ...bodyRows] = rows
  const headerHtml = headers.map((value) => `<th>${escapePreviewHtml(value)}</th>`).join('')
  const bodyHtml = bodyRows.map((row) => (
    `<tr>${row.map((value) => `<td>${escapePreviewHtml(value)}</td>`).join('')}</tr>`
  )).join('')
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    :root { color-scheme: light; font-family: Arial, sans-serif; color: #1f2937; background: #f5f7fa; }
    body { margin: 0; padding: 20px; }
    .csv { width: max-content; min-width: 100%; overflow: hidden; border: 1px solid #cbd5e1; background: white; box-shadow: 0 10px 30px rgba(15, 23, 42, .08); }
    table { border-collapse: collapse; font-size: 12px; }
    th, td { min-width: 120px; border: 1px solid #d8dee8; padding: 7px 9px; text-align: right; white-space: nowrap; }
    th { position: sticky; top: 0; z-index: 1; background: #365f9d; color: white; font-weight: 700; text-align: left; }
    th:first-child, td:first-child { position: sticky; left: 0; min-width: 150px; text-align: left; }
    th:first-child { z-index: 2; }
    td:first-child { background: white; }
    tbody tr:nth-child(even) td { background: #f8fafc; }
    tbody tr:nth-child(even) td:first-child { background: #f8fafc; }
  </style>
</head>
<body><div class="csv"><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div></body>
</html>`
}

// Loaded only when the CFO view is active, so the regular demo/studio bundle
// does not grow.
const CfoSummary = lazy(() => importWithReload(() => import('@/components/cfo/CfoSummary')))
const CfoAssumptionsPopover = lazy(() => importWithReload(() => import('@/components/cfo/CfoAssumptionsPopover')))

const OutputWorkspace = memo(function OutputWorkspace({ exportResultsRef, cfoMode = false }) {
  return (
    <div className="space-y-8">
      {cfoMode
        ? <Suspense fallback={null}><CfoSummary /></Suspense>
        : <SummaryDashboard />}
      {!cfoMode && <AllocationComparisonTable />}
      {!cfoMode && <CashBalanceScheduleTable />}
      <EmployeeDetails exportResultsRef={exportResultsRef} />
      <Visualizations />
    </div>
  )
})

function OutputsBlocked({ errors, onFocusError }) {
  const count = errors.length

  return (
    <div className="flex min-h-[18rem] items-start justify-center px-3 pt-12 lg:px-4">
      <div className="w-full max-w-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-destructive">Fix invalid inputs to update outputs</h2>
            <p className="leading-relaxed">
              Results will come back once {count === 1 ? 'that number is filled in correctly' : `${count} numbers are filled in correctly`}.
              Exports are off until then.
            </p>
            <ul className="mt-3 space-y-1.5 border-t border-destructive/20 pt-3 text-xs text-destructive/90">
              {errors.map((error) => (
                <li key={error.id}>
                  <button
                    type="button"
                    className="flex w-full gap-2 rounded px-1 py-1 text-left transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-destructive/30"
                    onClick={() => onFocusError(error)}
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive/70" />
                    <span>
                      <span className="font-medium underline underline-offset-2">{error.label}</span>: {error.message}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// These are revisitable workspace sections, not locked wizard steps.
function StageNav({ stage, hasEmployees, checkSummary, onSelect }) {
  const activeItemRef = useRef(null)

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [stage])

  return (
    <nav aria-label="Calculator steps" className="shrink-0 border-b border-border/70 bg-card px-3 lg:px-4">
      <div className="mx-auto flex h-11 w-full max-w-[1600px] items-stretch overflow-x-auto">
        {/* The auto margins center the tabs whenever they fit. Once the row is
            wider than the shell they collapse to zero, so the strip falls back to
            a normal left-aligned scroller instead of hiding its first tab. */}
        <div className="mx-auto flex items-stretch gap-1">
          {STAGE_ITEMS.map((item) => {
            const active = stage === item.key
            const disabled = !hasEmployees && item.key !== 'people'
            return (
              <button
                key={item.key}
                ref={active ? activeItemRef : undefined}
                type="button"
                onClick={() => onSelect(item.key)}
                disabled={disabled}
                aria-current={active ? 'page' : undefined}
                data-stage-nav={item.key}
                className={[
                  'relative flex shrink-0 items-center gap-2 px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50',
                  active
                    ? 'bg-muted/35 text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground' : '',
                ].join(' ')}
                title={disabled ? 'Add employees first' : undefined}
              >
                <span
                  aria-hidden="true"
                  className={[
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full border text-[10px] font-semibold tabular-nums',
                    active ? 'border-foreground bg-foreground text-background' : 'border-border bg-background text-muted-foreground',
                  ].join(' ')}
                >
                  {item.step}
                </span>
                {item.label}
                {item.key === 'checks' && checkSummary?.issueCount > 0 ? (
                  <span
                    className="inline-flex min-w-4 items-center justify-center rounded-full bg-amber-100 px-1 text-[9px] font-semibold leading-4 text-amber-900"
                    aria-label={`${checkSummary.issueCount} check ${checkSummary.issueCount === 1 ? 'issue' : 'issues'}`}
                  >
                    {checkSummary.issueCount}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>
    </nav>
  )
}

function PresetsPopover({ planKey = null }) {
  const applyPlanPreset = useStore((state) => state.applyPlanPreset)
  // Comparison columns pass their plan explicitly; the single-plan editor is
  // always Plan A.
  const targetKey = planKey || 'planA'
  const targetName = useStore((state) => planDisplayName(targetKey, state[targetKey]?.name))
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(null)

  const handleOpenChange = (next) => {
    setOpen(next)
    if (!next) setSelected(null)
  }

  const applyPreset = (preset) => {
    if (!applyPlanPreset(targetKey, getPresetSettings(preset))) return
    setOpen(false)
    setSelected(null)
    toast.success(`Applied preset to ${targetName}: ${preset.name}`)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" title={`Apply a preset to ${targetName}`} aria-label={`Apply a preset to ${targetName}`}>
          Presets<ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        {selected ? (
          <>
            <DialogHeader>
              <DialogTitle>{selected.name}</DialogTitle>
              <DialogDescription>{selected.description}</DialogDescription>
            </DialogHeader>
            <dl className="-mx-1 max-h-[50vh] divide-y divide-border overflow-y-auto px-1">
              {presetSettingRows(selected).map((row) => (
                <div key={row.label} className="flex items-baseline justify-between gap-4 py-2">
                  <dt className="text-xs text-muted-foreground">{row.label}</dt>
                  <dd className="text-right text-sm font-medium">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground">
              Applying replaces {targetName}&rsquo;s contribution, vesting, after-tax, and cash-balance settings, which you can still fine-tune afterward.
            </p>
            <DialogFooter className="gap-2 sm:justify-between">
              <Button variant="outline" size="sm" onClick={() => setSelected(null)}>
                <ChevronLeft className="mr-1 h-3.5 w-3.5" />Back
              </Button>
              <Button size="sm" onClick={() => applyPreset(selected)}>Apply to {targetName}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Apply a preset to {targetName}</DialogTitle>
              <DialogDescription>
                Common plan designs to start from. Pick one to preview its full settings, then apply it.
              </DialogDescription>
            </DialogHeader>
            <div className="-mx-1 max-h-[55vh] space-y-1.5 overflow-y-auto px-1">
              {PLAN_PRESETS.map((preset) => (
                <button key={preset.name} type="button" className="flex w-full items-center gap-3 rounded border border-border px-3 py-2 text-left transition-colors hover:bg-muted" onClick={() => setSelected(preset)}>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-medium">{preset.name}</span>
                    <span className="text-xs text-muted-foreground">{preset.description}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ClientPlanDialog({ open, onOpenChange, intent, locked = false, loginHref = null }) {
  const managing = intent === 'manage'
  const activeClientId = useClientBook((state) => state.activeClientId)
  const title = managing ? 'Manage clients' : activeClientId ? 'Duplicate client' : 'Name this client'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {managing
              ? 'Open, rename, or remove clients saved to your account.'
              : activeClientId
                ? 'Create a new client using the current plan settings.'
                : 'Give this plan a client name. Future plan changes save automatically.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[68vh] overflow-y-auto pr-1">
          <ClientBookBody
            locked={locked}
            loginHref={loginHref}
            intent={intent}
            onComplete={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PlanTemplatesDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Plan Templates</DialogTitle>
          <DialogDescription>Save a plan option once, then apply it to another client or comparison.</DialogDescription>
        </DialogHeader>
        <PlanChips manage savedPlansOnly showTemplateHeading={false} />
      </DialogContent>
    </Dialog>
  )
}

function LegalBar({ onContactClick, onPrivacyClick, footerActions = null }) {
  return (
    <footer className="shrink-0 border-t border-border bg-muted/30 px-3 py-1.5 text-[10px] leading-snug text-muted-foreground sm:px-5 sm:text-[11px]">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4">
        <p className="min-w-0 flex-1 truncate" title="© 2026 Retira LLC. Results are illustrative and are not legal, tax, or investment advice.">
          © 2026 Retira LLC. Results are illustrative and are not legal, tax, or investment advice.
        </p>
        <nav className="flex shrink-0 items-center gap-3" aria-label="Calculator footer navigation">
          {footerActions}
          <button type="button" onClick={onContactClick} className="transition-colors hover:text-foreground">
            Contact
          </button>
          <button type="button" onClick={onPrivacyClick} className="transition-colors hover:text-foreground">
            Privacy &amp; Terms
          </button>
        </nav>
      </div>
    </footer>
  )
}

// One-time nudge for phone-sized screens. Shared by every shell that renders
// CalculatorWorkspace (demo + studio), so it only needs to live here. Dismissal
// is remembered per session so it does not nag on every navigation.
function MobileWarningDialog() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.sessionStorage.getItem(MOBILE_WARNING_SEEN_KEY) === 'true') return
    if (!window.matchMedia('(max-width: 767px)').matches) return
    setOpen(true)
  }, [])

  const acknowledge = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(MOBILE_WARNING_SEEN_KEY, 'true')
    }
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) acknowledge() }}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Best experience on larger screens</DialogTitle>
          <DialogDescription>
            retira.app works best on a laptop or larger screen. You can keep going here, but
            some features may be limited.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={acknowledge}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const noop = () => {}
const ONBOARDING_HISTORY_VIEW = 'retiraOnboardingView'
const ONBOARDING_RESULTS_VIEW = 'results-summary'
const ONBOARDING_WORKSPACE_VIEW = 'workspace'

export default function CalculatorWorkspace({
  documentTitle = 'retira.app',
  onContactClick = noop,
  onLogoClick = noop,
  onPrivacyClick = noop,
  footerActions = null,
  loginHref = null,
  account = null,
  demoMode = false,
  // CFO view: the identical workspace, with the Summary section swapped for
  // the CFO cost summary (budget, exposure, net cost). Nothing else changes.
  cfoMode = false,
  studioHref = null,
}) {
  const employees = useStore((state) => state.employees)
  const censusMode = useStore((state) => state.censusMode)
  const workspaceMode = useStore((state) => state.workspaceMode)
  const calculatedEmployeeCount = useStore((state) => state.analysis.planA.calculatedEmployees.length)
  const recalculate = useStore((state) => state.recalculate)
  const inputErrors = useStore((state) => state.inputErrors)
  const planYear = useStore((state) => state.planA.planYear)
  const setJourneyFlag = useStore((state) => state.setJourneyFlag)
  const workspaceStage = useStore((state) => state.workspaceStage)
  const setWorkspaceStage = useStore((state) => state.setWorkspaceStage)
  const importPlanSettings = useStore((state) => state.importPlanSettings)
  const enableClientBook = useClientBook((state) => state.enableCloud)
  const refreshClients = useClientBook((state) => state.refresh)
  const clearActiveClient = useClientBook((state) => state.clearActive)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [exportLockOpen, setExportLockOpen] = useState(false)
  const [clientPlanIntent, setClientPlanIntent] = useState(null)
  const [planTemplatesOpen, setPlanTemplatesOpen] = useState(false)
  const [estimatorOpen, setEstimatorOpen] = useState(false)
  const [returnToChoices, setReturnToChoices] = useState(false)
  const [resumeOnboardingResults, setResumeOnboardingResults] = useState(false)
  const [onboardingRestartKey, setOnboardingRestartKey] = useState(0)
  const comparing = useStore(isComparing)
  const checkSummary = useStore(modeledCheckSummaryForState)
  // Demo users see the AI features (estimator, plan-doc import) but they are
  // login-gated. 'plan-doc' | 'estimate' | null.
  const [aiLock, setAiLock] = useState(null)
  const outputScrollRef = useRef(null)
  const exportResultsRef = useRef(null)
  const settingsFileInputRef = useRef(null)
  const cloudPlanInitializedRef = useRef(false)
  // Resume only when there is a real census to resume. Custom and estimate
  // rows are user data; default example rows count only after the user has
  // explicitly completed a workflow. Empty or stale persisted state always
  // returns to the starting choices.
  const [onboardingActive, setOnboardingActive] = useState(
    () => {
      const onboardingDone =
        typeof window !== 'undefined' &&
        window.localStorage.getItem(ONBOARDING_DONE_KEY) === 'true'
      const hasResumableCensus =
        employees.length > 0 &&
        (censusMode !== CENSUS_MODES.EXAMPLE || onboardingDone)
      return demoMode || FORCE_ONBOARDING || !hasResumableCensus
    }
  )
  const hasEmployees = employees.length > 0

  // Signed-in work is cloud-first: turn on the census-free client index,
  // refresh it once, and recover the account draft only when it is safer/newer
  // than the browser copy. This never uploads employee rows.
  useEffect(() => {
    if (!account || !supabaseEnv.isReady || cloudPlanInitializedRef.current) return
    cloudPlanInitializedRef.current = true
    const initializeCloudPlan = async () => {
      try {
        if (useClientBook.getState().enabled) await refreshClients()
        else await enableClientBook()
        await restorePlanAutosave(useStore, useClientBook)
      } catch (error) {
        console.error('Could not initialize account plan saving:', error)
        useClientBook.getState().setSyncStatus('error', { error })
      }
    }
    void initializeCloudPlan()
  }, [account, enableClientBook, refreshClients])
  const prospectWorkflow = workspaceMode === 'estimate'
  const demoWorkflow = workspaceMode !== 'estimate' && censusMode === CENSUS_MODES.EXAMPLE
  const inputErrorList = Object.entries(inputErrors)
    .map(([id, error]) => ({
      id,
      message: typeof error === 'string' ? error : error?.message,
      label: typeof error === 'string' ? id : (error?.label || id),
      target: typeof error === 'string' ? { validationId: id } : (error?.target || { validationId: id }),
    }))
    .filter(error => error.message)
    .sort((a, b) => a.label.localeCompare(b.label))
  const inputErrorCount = inputErrorList.length
  const hasInputErrors = inputErrorCount > 0
  const stage = workspaceStage
  useEffect(() => {
    track('stage_view')
  }, [stage])

  // The /cfo deep-link lands on Results with the finance view already on, but
  // the workspace is otherwise the normal, fully navigable studio.
  useEffect(() => {
    if (cfoMode) setWorkspaceStage('results')
    // Seed once on mount; navigation is free afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // URL-hash routing (§7 rule 1): the hash is canonical when present; the
  // persisted stage is only the initial-load fallback. All changes flow
  // through the single setWorkspaceStage action, and state→hash mirroring
  // uses replaceState, which never re-fires hashchange, so there is no sync loop.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const applyHash = () => {
      const fromHash = window.location.hash.replace(/^#/, '')
      if (WORKSPACE_STAGES.includes(fromHash)) setWorkspaceStage(fromHash)
    }
    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => window.removeEventListener('hashchange', applyHash)
  }, [setWorkspaceStage])
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.hash.replace(/^#/, '') !== workspaceStage) {
      window.history.replaceState(window.history.state, '', `#${workspaceStage}`)
    }
  }, [workspaceStage])

  // Show Census and Show Results open a workspace view without discarding the
  // summary that launched it. Browser Back restores that summary, and Forward
  // returns to the workspace, instead of leaving the calculator entirely.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const restoreOnboardingView = (event) => {
      const historyView = event.state?.[ONBOARDING_HISTORY_VIEW]
      if (historyView === ONBOARDING_RESULTS_VIEW) {
        if (!useStore.getState().analysis.planA.metrics) {
          setResumeOnboardingResults(false)
          setOnboardingActive(false)
          setWorkspaceStage('people')
          window.history.replaceState(
            {
              ...(event.state || {}),
              [ONBOARDING_HISTORY_VIEW]: ONBOARDING_WORKSPACE_VIEW,
            },
            '',
            '#people',
          )
          return
        }
        setEstimatorOpen(false)
        setReturnToChoices(false)
        setResumeOnboardingResults(true)
        setOnboardingRestartKey((key) => key + 1)
        setOnboardingActive(true)
        return
      }
      if (historyView === ONBOARDING_WORKSPACE_VIEW) {
        setResumeOnboardingResults(false)
        setOnboardingActive(false)
      }
    }
    window.addEventListener('popstate', restoreOnboardingView)
    return () => window.removeEventListener('popstate', restoreOnboardingView)
  }, [setWorkspaceStage])

  // Stages beyond Census need employee data; land back on Census when it empties.
  useEffect(() => {
    if (!hasEmployees && workspaceStage !== 'people') setWorkspaceStage('people')
  }, [hasEmployees, workspaceStage, setWorkspaceStage])

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  const completeOnboarding = (initialStage) => {
    setEstimatorOpen(false)
    const requestedStage = WORKSPACE_STAGES.includes(initialStage) ? initialStage : null
    const destinationStage = requestedStage === 'results' && !useStore.getState().analysis.planA.metrics
      ? 'people'
      : requestedStage
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ONBOARDING_DONE_KEY, 'true')
      if (destinationStage) {
        const summaryState = {
          ...(window.history.state || {}),
          [ONBOARDING_HISTORY_VIEW]: ONBOARDING_RESULTS_VIEW,
        }
        window.history.replaceState(summaryState, '', window.location.href)
        window.history.pushState(
          {
            ...summaryState,
            [ONBOARDING_HISTORY_VIEW]: ONBOARDING_WORKSPACE_VIEW,
          },
          '',
          `#${destinationStage}`,
        )
      }
    }
    // A fresh workspace should not be able to undo back through the choices
    // made during onboarding.
    useStore.temporal.getState().clear()
    if (destinationStage) setWorkspaceStage(destinationStage)
    setReturnToChoices(false)
    setResumeOnboardingResults(false)
    setOnboardingActive(false)
  }

  const openStartingChoices = () => {
    // A previously opened estimate form is modal. Always close it before
    // changing screens so it cannot sit invisibly above the choices.
    setEstimatorOpen(false)
    setReturnToChoices(true)
    setResumeOnboardingResults(false)
    setOnboardingRestartKey((key) => key + 1)
    setOnboardingActive(true)
  }

  useEffect(() => {
    if (employees.length === 0 || calculatedEmployeeCount > 0) return
    recalculate()
  }, [calculatedEmployeeCount, employees.length, recalculate])

  // A newly populated census starts with an untouched plan so store-level plan
  // initialization can apply the correct example-data defaults.
  const prevHasEmployeesRef = useRef(hasEmployees)
  useEffect(() => {
    const wasEmpty = !prevHasEmployeesRef.current
    prevHasEmployeesRef.current = hasEmployees
    if (wasEmpty && hasEmployees) {
      setJourneyFlag('planTouched', false)
    }
  }, [hasEmployees, setJourneyFlag])

  const handleFocusInputError = (error) => {
    const target = error.target || { validationId: error.id }
    setWorkspaceStage(target.kind === 'employee' ? 'people' : 'plan')

    const focusTarget = () => {
      if (target.kind === 'employee') {
        window.dispatchEvent(new CustomEvent(FOCUS_EMPLOYEE_INPUT_EVENT, { detail: target }))
        return
      }

      if (!target.validationId) return
      const selector = `[data-validation-id="${escapeCssValue(target.validationId)}"]`
      const element = document.querySelector(selector)
      if (!element) return
      element.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
      flashInputTarget(element)
      window.setTimeout(() => element.focus({ preventScroll: true }), 150)
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(focusTarget)
    })
  }

  const handlePdfExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generatePdf } = await importWithReload(() => import('@/lib/pdf'))
      await generatePdf(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'pdf', { cfoMode, reportSections: REPORT_SECTIONS }),
        reason: 'pdf_generation_error',
      })
      toast.error(error.message || 'Failed to generate PDF')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleProspectExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generateProspectOnePager } = await importWithReload(() => import('@/lib/pdf'))
      await generateProspectOnePager(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'prospect_one_pager', { cfoMode }),
        reason: 'prospect_one_pager_generation_error',
      })
      toast.error(error.message || 'Failed to generate the prospect one-pager')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleProspectHtmlExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generateProspectInteractiveReport } = await importWithReload(() => import('@/lib/htmlExport/prospectInteractive'))
      await generateProspectInteractiveReport(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'prospect_html', { cfoMode }),
        reason: 'prospect_html_generation_error',
      })
      toast.error(error.message || 'Failed to generate the interactive prospect one-pager')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  // Same report data as the PDF, exported as a standalone interactive HTML
  // file that embeds the site's own output components (recharts, drag/lasso
  // selection, employee-group dialogs), driven by the same customizer.
  const handleHtmlExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generateInteractiveReport } = await importWithReload(() => import('@/lib/htmlExport/interactive'))
      await generateInteractiveReport(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'html', { cfoMode, reportSections: HTML_REPORT_SECTIONS }),
        reason: 'html_generation_error',
      })
      toast.error(error.message || 'Failed to generate the interactive report')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  // The 100-employee Demo keeps the original, data-rich interactive report:
  // the complete Summary Dashboard followed by the full chart library.
  const handleDetailedDemoHtmlExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generateDetailedDemoInteractiveReport } = await importWithReload(() => import('@/lib/htmlExport/interactive'))
      await generateDetailedDemoInteractiveReport(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'demo-html', { cfoMode }),
        reason: 'demo_html_generation_error',
      })
      toast.error(error.message || 'Failed to generate the detailed Demo report')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  // Prospect Mode uses the same complete interactive report as a full census,
  // while retaining the estimate badge and estimated-census disclosure.
  const handleDetailedHtmlExport = async () => {
    setIsGeneratingPdf(true)
    try {
      const { generateDetailedInteractiveReport } = await importWithReload(() => import('@/lib/htmlExport/interactive'))
      await generateDetailedInteractiveReport(planYear)
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'detailed-html', { cfoMode }),
        reason: 'detailed_html_generation_error',
      })
      toast.error(error.message || 'Failed to generate the detailed report')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  // Export actions are visible to everyone, but the two downloads require an account.
  const isAuthenticated = Boolean(account)

  const handleDownloadPlanSettings = () => {
    exportPlanSettings(useStore.getState())
    trackExportEvent('download', exportDownloadProps(useStore.getState(), 'plan_settings_json', { cfoMode }))
    toast.success('Plan settings downloaded')
  }

  const handleImportPlanSettings = () => settingsFileInputRef.current?.click()

  const handleSettingsFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      importPlanSettings(await parsePlanSettingsFile(file))
      setWorkspaceStage('plan')
      toast.success('Plan settings imported')
    } catch (error) {
      toast.error('Failed to import plan settings', { description: error.message })
    } finally {
      if (settingsFileInputRef.current) settingsFileInputRef.current.value = ''
    }
  }

  const handleSaveClientPlanAs = () => {
    if (!supabaseEnv.isReady) {
      handleDownloadPlanSettings()
      return
    }
    setClientPlanIntent('save')
  }

  const handleNewClient = async () => {
    await flushPlanAutosave()
    clearActiveClient()
    openStartingChoices()
  }

  const handleExportCsv = async () => {
    if (!isAuthenticated) {
      trackExportEvent('blocked', {
        format: 'results_csv',
        reason: 'unauthenticated',
        ...workspaceExportProps(useStore.getState(), { cfoMode }),
      })
      setExportLockOpen(true); return
    }
    if (hasInputErrors) {
      trackExportEvent('blocked', {
        format: 'results_csv',
        reason: 'input_errors',
        inputErrorCount,
        ...workspaceExportProps(useStore.getState(), { cfoMode }),
      })
      toast.error('Fix invalid inputs before exporting'); return
    }
    try {
      const { createCalculationsCsvArtifact } = await importWithReload(() => import('@/lib/csv'))
      const state = useStore.getState()
      const planKey = 'planA'
      const calculatedEmployees = state.analysis?.[planKey]?.calculatedEmployees || []
      if (!calculatedEmployees.length) throw new Error('No calculated employee results are available.')
      const artifact = createCalculationsCsvArtifact(calculatedEmployees, {
        estimate: state.workspaceMode === 'estimate',
      })
      downloadFile(artifact.content, artifact.fileName, artifact.mimeType)
      trackExportEvent('download', exportDownloadProps(state, 'results_csv', { cfoMode }))
    } catch (error) {
      toast.error(error.message || 'Failed to generate the results CSV')
    }
  }
  const handleExportProspectSpreadsheet = async () => {
    if (!isAuthenticated) {
      trackExportEvent('blocked', {
        ...exportDownloadProps(useStore.getState(), 'prospect_spreadsheet', { cfoMode }),
        reason: 'unauthenticated',
      })
      setExportLockOpen(true)
      return false
    }
    if (hasInputErrors) {
      toast.error('Fix invalid inputs before exporting')
      return false
    }
    setIsGeneratingPdf(true)
    try {
      const { generateProspectSpreadsheet } = await importWithReload(() => import('@/lib/prospectSpreadsheet'))
      await generateProspectSpreadsheet(planYear)
      trackExportEvent('download', exportDownloadProps(useStore.getState(), 'prospect_spreadsheet', { cfoMode }))
      return true
    } catch (error) {
      trackExportEvent('failed', {
        ...exportDownloadProps(useStore.getState(), 'prospect_spreadsheet', { cfoMode }),
        reason: 'prospect_spreadsheet_generation_error',
      })
      toast.error(error.message || 'Failed to generate the prospect spreadsheet')
      return false
    } finally {
      setIsGeneratingPdf(false)
    }
  }
  const handlePreviewReport = async (format) => {
    if (!isAuthenticated) {
      trackExportEvent('blocked', {
        ...workspaceExportProps(useStore.getState(), { cfoMode }),
        format: `${format}_preview`,
        reason: 'unauthenticated',
      })
      setExportLockOpen(true)
      return null
    }
    if (hasInputErrors) {
      toast.error('Fix invalid inputs before previewing')
      return null
    }

    setIsGeneratingPdf(true)
    try {
      if (['pdf', 'detailed-pdf'].includes(format)) {
        const [pdfModule, pdfjs] = await Promise.all([
          importWithReload(() => import('@/lib/pdf')),
          import('pdfjs-dist'),
        ])
        const artifact = format === 'pdf'
          ? await pdfModule.createProspectOnePagerArtifact(planYear)
          : await pdfModule.createPdfArtifact(planYear)
        pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
        const document = await pdfjs.getDocument({ data: new Uint8Array(await artifact.blob.arrayBuffer()) }).promise
        const totalPages = document.numPages
        const imageUrls = []
        const imageSizes = []
        const baseScale = format === 'pdf' ? 1.5 : 1.1
        const scale = baseScale * Math.min(window.devicePixelRatio || 1, format === 'pdf' ? 2 : 1.5)
        // Render every page so the in-app preview matches the generated artifact
        // and can be reviewed as one continuous, vertically scrollable document.
        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
          const page = await document.getPage(pageNumber)
          const viewport = page.getViewport({ scale })
          const canvas = window.document.createElement('canvas')
          canvas.width = Math.ceil(viewport.width)
          canvas.height = Math.ceil(viewport.height)
          const canvasContext = canvas.getContext('2d')
          if (!canvasContext) throw new Error('The PDF preview could not be rendered in this browser.')
          await page.render({ canvasContext, viewport }).promise
          imageUrls.push(canvas.toDataURL('image/png'))
          imageSizes.push({ width: canvas.width, height: canvas.height })
          if (typeof page.cleanup === 'function') page.cleanup()
        }
        if (typeof document.destroy === 'function') {
          await document.destroy()
        } else if (typeof document.cleanup === 'function') {
          document.cleanup()
        }
        return { ...artifact, kind: 'pdf', imageUrls, imageSizes, totalPages, content: artifact.blob }
      }
      if (format === 'html') {
        const { createProspectInteractiveArtifact } = await importWithReload(() => import('@/lib/htmlExport/prospectInteractive'))
        const artifact = await createProspectInteractiveArtifact(planYear)
        return { ...artifact, kind: 'html', content: artifact.html }
      }
      if (format === 'detailed-html') {
        const { createInteractiveReportArtifact } = await importWithReload(() => import('@/lib/htmlExport/interactive'))
        const artifact = await createInteractiveReportArtifact(planYear, {
          detailed: prospectWorkflow,
          detailedDemo: demoWorkflow,
        })
        return { ...artifact, kind: 'html', content: artifact.html }
      }
      if (format === 'csv') {
        const [{ createCalculationsCsvArtifact }, papaModule] = await Promise.all([
          importWithReload(() => import('@/lib/csv')),
          import('papaparse'),
        ])
        const state = useStore.getState()
        const planKey = 'planA'
        const calculatedEmployees = state.analysis?.[planKey]?.calculatedEmployees || []
        if (!calculatedEmployees.length) throw new Error('No calculated employee results are available.')
        const artifact = createCalculationsCsvArtifact(calculatedEmployees, {
          estimate: state.workspaceMode === 'estimate',
        })
        const Papa = papaModule.default || papaModule
        const rows = Papa.parse(artifact.content, { skipEmptyLines: true }).data
        return {
          ...artifact,
          kind: 'csv',
          html: buildCsvPreviewHtml(rows),
        }
      }

      const { createProspectSpreadsheetArtifact } = await importWithReload(() => import('@/lib/prospectSpreadsheet'))
      const artifact = await createProspectSpreadsheetArtifact(planYear)
      return {
        ...artifact,
        kind: 'spreadsheet',
        html: artifact.previewHtml,
        content: artifact.bytes,
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate the report preview')
      return null
    } finally {
      setIsGeneratingPdf(false)
    }
  }
  const handleDownloadReport = async (format = 'pdf') => {
    if (!isAuthenticated) {
      trackExportEvent('blocked', {
        ...workspaceExportProps(useStore.getState(), { cfoMode }),
        format,
        reason: 'unauthenticated',
      })
      setExportLockOpen(true)
      return false
    }
    if (hasInputErrors) {
      trackExportEvent('blocked', {
        ...workspaceExportProps(useStore.getState(), { cfoMode }),
        format,
        reason: 'input_errors',
        inputErrorCount,
      })
      toast.error('Fix invalid inputs before exporting')
      return false
    }
    const succeeded = format === 'demo-html'
      ? await handleDetailedDemoHtmlExport()
      : format === 'detailed-html'
        ? await handleDetailedHtmlExport()
      : format === 'html'
        ? await handleHtmlExport()
      : format === 'prospect-html'
        ? await handleProspectHtmlExport()
      : format === 'prospect'
        ? await handleProspectExport()
        : await handlePdfExport()
    if (succeeded) {
      trackExportEvent('download', exportDownloadProps(useStore.getState(), format, {
        cfoMode,
        reportSections: format === 'html'
          ? HTML_REPORT_SECTIONS
          : ['prospect', 'prospect-html', 'demo-html', 'detailed-html'].includes(format) ? [] : REPORT_SECTIONS,
      }))
    }
    return succeeded
  }
  const handleArtifactDownload = (formatId) => {
    const format = {
      'detailed-pdf': 'pdf',
      'detailed-html': demoWorkflow ? 'demo-html' : prospectWorkflow ? 'detailed-html' : 'html',
      'prospect-pdf': 'prospect',
      'prospect-html': 'prospect-html',
      spreadsheet: 'prospect_spreadsheet',
      csv: 'results_csv',
    }[formatId] || formatId
    trackExportEvent('download', exportDownloadProps(useStore.getState(), format, {
      cfoMode,
      reportSections: format === 'html'
        ? HTML_REPORT_SECTIONS
        : ['prospect', 'prospect-html', 'demo-html', 'detailed-html', 'prospect_spreadsheet', 'results_csv'].includes(format)
          ? []
          : REPORT_SECTIONS,
    }))
  }

  // Navigation helpers: each jumps to its stage.
  const handleGoToPeople = () => setWorkspaceStage('people')
  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      {/* Long enough to actually read a toast with a description; a close
          button lets the reader dismiss early instead of racing the timer. */}
      <Toaster
        position="bottom-left"
        theme="light"
        duration={6500}
        visibleToasts={3}
        closeButton
        gap={6}
        offset={12}
        toastOptions={{
          style: {
            fontFamily: 'Merriweather Variable, Merriweather Fallback, Georgia, serif',
            padding: '10px 12px',
            minHeight: '44px',
            fontSize: '13px',
            lineHeight: '1.4',
            maxWidth: '340px',
            background: '#fafaf8',
            color: '#1c1917',
            borderColor: '#e8e5e0',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          },
        }}
      />
      <MobileWarningDialog />
      <UtilityDialogs isAuthenticated={Boolean(account)} loginHref={loginHref} />
      <input
        ref={settingsFileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={handleSettingsFileChange}
        className="hidden"
      />
      <FeatureLockDialog
        open={exportLockOpen}
        onOpenChange={setExportLockOpen}
        title="Log in to export"
        description="Downloading the report (PDF or interactive report) and results CSV is available to signed-in users."
        loginHref={loginHref}
      />
      <FeatureLockDialog
        open={Boolean(aiLock)}
        onOpenChange={(next) => { if (!next) setAiLock(null) }}
        title={aiLock === 'estimate' ? 'Log in to estimate a plan' : 'Log in to import from a plan document'}
        description={aiLock === 'estimate'
          ? 'The estimator (with AI-assisted assumptions) is available to signed-in users. It models a full plan from rough numbers without an employee file.'
          : 'AI extraction of plan settings from adoption agreements and SPDs is available to signed-in users.'}
        loginHref={loginHref}
      />

      <ClientPlanDialog
        open={Boolean(clientPlanIntent)}
        onOpenChange={(open) => { if (!open) setClientPlanIntent(null) }}
        intent={clientPlanIntent || 'manage'}
        locked={!isAuthenticated}
        loginHref={loginHref}
      />
      <PlanTemplatesDialog open={planTemplatesOpen} onOpenChange={setPlanTemplatesOpen} />

      <div className="shrink-0">
        <Header
          hasEmployees={hasEmployees}
          stage={stage}
          onLogoClick={onLogoClick}
          onContactClick={onContactClick}
          onPrivacyClick={onPrivacyClick}
          loginHref={loginHref}
          account={account}
          onSavePlanAs={!onboardingActive ? handleSaveClientPlanAs : null}
          onManageClients={!onboardingActive ? () => setClientPlanIntent('manage') : null}
          onNewClient={!onboardingActive ? handleNewClient : null}
          onOpenPlanTemplates={!onboardingActive ? () => setPlanTemplatesOpen(true) : null}
          onImportPlanSettings={!onboardingActive ? handleImportPlanSettings : null}
          onDownloadPlanSettings={!onboardingActive ? handleDownloadPlanSettings : null}
          onRetrySave={retryPlanAutosave}
          showWorkspaceActions={!onboardingActive}
        />
      </div>

      {!onboardingActive && <EstimateModeBanner />}

      {onboardingActive && (
        <OnboardingFlow
          onComplete={completeOnboarding}
          onStartEstimate={() => {
            if (!account) {
              setAiLock('estimate')
              return
            }
            setEstimatorOpen(true)
          }}
          forceOnboarding={FORCE_ONBOARDING}
          holdAtChoices={demoMode || returnToChoices}
          restartKey={onboardingRestartKey}
          resumeAtResults={resumeOnboardingResults}
        />
      )}

      <EstimatorDialog open={estimatorOpen} onOpenChange={setEstimatorOpen} />

      {/* Kept mounted (hidden) during onboarding so DataInput's upload input,
          parsing, and dialog flows stay available to the guided steps. */}
        <main className={onboardingActive ? 'hidden' : 'flex min-h-0 flex-1 flex-col overflow-hidden bg-background'}>
          <StageNav
            stage={stage}
            hasEmployees={hasEmployees}
            checkSummary={checkSummary}
            onSelect={setWorkspaceStage}
          />

          {(stage === 'people' || onboardingActive) && (
            <div className="relative flex-1 overflow-y-auto overscroll-contain px-3 pb-5 lg:px-4">
              <div className="mx-auto w-full max-w-[1600px] pt-3">
                <DataInput
                  demoMode={demoMode}
                  studioHref={studioHref}
                  loginHref={loginHref}
                />
              </div>
            </div>
          )}

          {stage === 'plan' && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 lg:px-4">
              <div className="mx-auto h-full min-h-0 w-full max-w-[1600px] py-2">
                {comparing ? (
                  <PlanInputComparison
                    demoMode={demoMode}
                    toolbarActionsForPlan={(planKey) => <PresetsPopover planKey={planKey} />}
                  />
                ) : (
                  <PlanSettings
                    demoMode={demoMode}
                    toolbarActions={<PresetsPopover />}
                  />
                )}
              </div>
            </div>
          )}

          {stage === 'checks' && (
            <div className="relative flex-1 overflow-y-auto overscroll-contain px-3 pb-5 lg:px-4">
              {hasInputErrors
                ? <OutputsBlocked errors={inputErrorList} onFocusError={handleFocusInputError} />
                : <ChecksStage />}
            </div>
          )}

          {stage === 'results' && (hasEmployees ? (
            <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <SectionNav
                items={outputNavItems(cfoMode)}
                disabled={hasInputErrors}
                scrollRootRef={outputScrollRef}
                actions={!comparing && cfoMode ? (
                  <Suspense fallback={null}>
                    <CfoAssumptionsPopover />
                  </Suspense>
                ) : null}
              />
              <div ref={outputScrollRef} className="relative flex-1 overflow-y-auto overscroll-contain px-3 pb-5 pt-4 lg:px-4">
                <div className="mx-auto w-full max-w-[1600px]">
                  {hasInputErrors
                    ? <OutputsBlocked errors={inputErrorList} onFocusError={handleFocusInputError} />
                    : <OutputWorkspace exportResultsRef={exportResultsRef} cfoMode={cfoMode} />}
                </div>
              </div>
            </section>
          ) : (
            <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex h-full flex-col items-center gap-4 px-3 pt-24 lg:px-4">
                <p className="text-2xl text-muted-foreground">Add employees to see results</p>
                <Button type="button" variant="outline" onClick={handleGoToPeople}>
                  <Users className="mr-2 h-4 w-4" />
                  Add Employees
                </Button>
              </div>
            </section>
          ))}

          {stage === 'reports' && (
            <div className="relative flex min-h-0 flex-1 overflow-hidden px-3 pb-3 lg:px-4">
              <ReportStudio
                hasInputErrors={hasInputErrors}
                isAuthenticated={isAuthenticated}
                isGenerating={isGeneratingPdf}
                onExportCsv={handleExportCsv}
                onExportSpreadsheet={handleExportProspectSpreadsheet}
                onPreviewReport={handlePreviewReport}
                onDownloadReport={handleDownloadReport}
                onArtifactDownload={handleArtifactDownload}
                comparing={comparing}
                checkSummary={checkSummary}
                onReviewChecks={() => setWorkspaceStage('checks')}
                detailedHtmlDownloadFormat={
                  demoWorkflow
                    ? 'demo-html'
                    : prospectWorkflow
                      ? 'detailed-html'
                      : 'html'
                }
                htmlIsFullDetail={demoWorkflow || prospectWorkflow}
              />
            </div>
          )}
        </main>

      <LegalBar onContactClick={onContactClick} onPrivacyClick={onPrivacyClick} footerActions={footerActions} />
    </div>
  )
}
